import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import MagneticButton from "../components/MagneticButton";
import { TIMELINE, TEAM, HERO_IMAGES, waLink } from "../lib/constants";

const VALUES = [
  { t: "B2B Leadership", d: "Built exclusively for retailers — not an offshoot of retail." },
  { t: "Streamlined Procurement", d: "One dashboard, thousands of designs, vetted makers." },
  { t: "Personalized Recommendations", d: "Curated edits matched to your store's DNA." },
  { t: "Logistics", d: "Door-to-door visibility, insured and documented." },
  { t: "Financing", d: "Flexible terms so working capital stays with you." },
];

export default function About() {
  return (
    <>
      <section data-testid="about-banner" className="relative h-[75vh] overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.aboutBanner} alt="" className="w-full h-full object-cover opacity-30" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6F0]/85 via-[#FAF6F0]/85 to-[#FAF6F0]" />
        </div>
        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-12 pb-20">
            <div className="caption text-[#B5896E] mb-5">About Blue Velvet</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] max-w-4xl">
              Three decades of craft.
              <br />
              <em className="italic text-[#B5896E]">One modern platform.</em>
            </h1>
          </div>
        </div>
      </section>

      <section data-testid="about-story" className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <Reveal as="div" className="lg:col-span-5 lg:sticky lg:top-28">
            <SectionHeading eyebrow="Our Story" title={<>A team of gemologists, designers, karigars — and <em className="italic text-[#B5896E]">techies.</em></>} />
          </Reveal>
          <Reveal delay={120} as="div" className="lg:col-span-7 space-y-6 text-[#4A3F35]/75 text-base lg:text-lg leading-relaxed">
            <p>We are a team of experienced gemologists, designers, karigars and techies with 30+ years of expertise, providing a one-stop-shop for all your jewelry needs — discovery, design, sourcing, logistics, financing.</p>
            <p>Our mission is simple: simplify the sourcing and procurement process for jewelry retailers — so independent jewelers can compete with bigger brands in quality, pricing and variety.</p>
            <p>Blue Velvet is an extension of a 45-year family legacy in manufacturing, printing, design and finance across Karnataka — now, engineered for a global trade.</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-values" className="py-20 bg-[#E8DDD0]/25 border-y border-[#B5896E]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="Values" title="What we stand for" /></Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 80}>
                <div className="p-8 gold-border bg-[#FFFFFF]/50 h-full">
                  <div className="text-[#B5896E] font-display text-3xl mb-4">0{i + 1}</div>
                  <h4 className="font-display text-lg mb-2">{v.t}</h4>
                  <p className="text-sm text-[#4A3F35]/60 leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-timeline" className="py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="Journey" title={<>A legacy, <em className="italic text-[#B5896E]">compounding.</em></>} /></Reveal>
          <div className="relative mt-16 pl-10 md:pl-0">
            <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-px bg-[#B5896E]/25" />
            <div className="space-y-12">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 100}>
                  <div className={`relative grid grid-cols-1 md:grid-cols-2 gap-10 items-start ${i % 2 === 0 ? "" : "md:[&>div:first-child]:order-2"}`}>
                    <div className={`absolute left-3 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#B5896E] shadow-[0_0_18px_rgba(201,168,76,0.8)]`} />
                    <div className={`pl-8 md:pl-0 ${i % 2 === 0 ? "md:text-right md:pr-14" : "md:pl-14"}`}>
                      <div className="font-display text-4xl text-[#B5896E]">{t.year}</div>
                    </div>
                    <div className={`pl-8 md:pl-0 ${i % 2 === 0 ? "md:pl-14" : "md:text-right md:pr-14"}`}>
                      <h4 className="font-display text-2xl text-[#4A3F35] mb-2">{t.title}</h4>
                      <p className="text-sm text-[#4A3F35]/65 leading-relaxed">{t.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="about-team" className="py-24 lg:py-32 bg-[#E8DDD0]/20 border-y border-[#B5896E]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="The Team" title="Leadership" /></Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {TEAM.map((m, i) => (
              <Reveal key={m.name} delay={i * 100}>
                <div data-testid={`team-card-${i}`} className="tilt-card gold-border bg-[#FFFFFF]/60 p-8">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#B5896E]/10 mb-6">
                    <span className="font-display text-xl text-[#B5896E]">{m.name[0]}</span>
                  </div>
                  <div className="caption text-[#B5896E] mb-2">{m.role}</div>
                  <h3 className="font-display text-2xl mb-4">{m.name}</h3>
                  <p className="text-sm text-[#4A3F35]/65 leading-relaxed">{m.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-promoters" className="py-24 lg:py-32">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal as="div" className="lg:col-span-5">
            <div className="relative aspect-[4/5] gold-border bg-white overflow-hidden rounded-sm soft-shadow">
              <img
                data-testid="promoters-image"
                src="https://images.unsplash.com/photo-1742137188729-d46186ac5733?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBqZXdlbHJ5JTIwZmxhdGxheSUyMGRhcmt8ZW58MHx8fHwxNzc4MDQxNTkwfDA&ixlib=rb-4.1.0&q=85"
                alt="Mattar Anand Kini & Sons — heritage craftsmanship"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FAF6F0]/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 caption text-[#4A3F35]/85 bg-white/85 backdrop-blur px-4 py-3 rounded-sm">
                Heritage karigars · Udupi · Surat · Bangalore
              </div>
            </div>
          </Reveal>
          <Reveal as="div" delay={120} className="lg:col-span-7">
            <div className="caption text-[#B5896E] mb-5 flex items-center gap-3"><span className="w-8 h-[1px] bg-[#B5896E]" /> Promoters</div>
            <h2 className="font-display text-4xl lg:text-5xl leading-tight mb-6">Mattar Anand Kini & Sons</h2>
            <p className="text-base lg:text-lg text-[#4A3F35]/70 leading-relaxed max-w-xl">
              45 years in manufacturing, printing, design and finance across Udupi, Mangalore, Shimoga and Davangere — the operational bedrock behind Blue Velvet Lifestyle Private Limited.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {["Udupi", "Surat", "Bangalore"].map((b) => (
                <div key={b} className="text-center p-4 gold-border bg-white/60">
                  <div className="caption text-[#B5896E]">Branch</div>
                  <div className="font-display text-lg text-[#4A3F35] mt-1">{b}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-cta" className="py-20 lg:py-24 bg-gradient-to-r from-[#FAF6F0] via-[#E8DDD0]/50 to-[#FAF6F0]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 text-center">
          <h3 className="font-display text-3xl lg:text-4xl mb-8">Send us your requirements</h3>
          <div className="flex flex-wrap items-center justify-center gap-5">
            <Link to="/contact"><MagneticButton testId="about-cta-contact" variant="primary">Open a conversation</MagneticButton></Link>
            <MagneticButton testId="about-cta-whatsapp" href={waLink()} variant="outline">WhatsApp us</MagneticButton>
          </div>
        </div>
      </section>
    </>
  );
}
